import { Badge, Button, Heading, Select, Text } from '@radix-ui/themes';
import { Plus } from 'lucide-react';
import { isHubertAdminUser } from 'types/user.types';
import { useThemeEditor } from '../hooks/useThemeEditor';
import { EditorField } from './EditorField';

export const ThemeSidebar = () => {
  const { user, clientId, allThemes, activeThemeId, themeOverrides, addTheme, sortedClients, searchParams, navigate } =
    useThemeEditor();

  return (
    <div className="flex w-80 flex-col border-r border-gray-200 bg-gray-50/50">
      <div className="flex flex-col gap-4 border-b bg-white p-4">
        {isHubertAdminUser(user) && (
          <EditorField label="Select Client">
            <Select.Root
              value={clientId}
              onValueChange={(val) => {
                navigate({
                  pathname: '/themes/hubert-default',
                  search: `clientId=${val}`,
                });
              }}
            >
              <Select.Trigger className="w-full!" placeholder="Pick a client..." />
              <Select.Content position="popper">
                {sortedClients.map((c: any) => (
                  <Select.Item key={c._id} value={c._id}>
                    {c.org_name}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select.Root>
          </EditorField>
        )}
        <div className="mt-2 flex items-center justify-between">
          <Heading size="3">Themes</Heading>
          <Button
            variant="soft"
            onClick={addTheme}
            disabled={!clientId}
            className={`flex items-center gap-1 rounded-lg ${!clientId ? '' : 'cursor-pointer!'}`}
          >
            <Plus size={16} />
            Add
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 pb-20">
        <div className="flex flex-col gap-2">
          {allThemes.map((t: any) => {
            const isActive = activeThemeId === t.id;
            const isModified = !!themeOverrides[t.id!];
            return (
              <button
                key={t.id}
                onClick={
                  !clientId
                    ? undefined
                    : () => navigate({ pathname: `/themes/${t.id}`, search: searchParams.toString() })
                }
                disabled={!clientId}
                className={`flex w-full items-center gap-2 rounded-xl border p-2 text-left transition-all ${
                  isActive ? 'border bg-white shadow-md' : 'bg-gray-100 hover:bg-gray-200'
                } ${!clientId ? 'cursor-not-allowed opacity-50' : 'cursor-pointer!'}`}
                style={{ borderColor: isActive ? t.brandColor : 'transparent' }}
              >
                <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-gray-100 bg-white p-1 shadow-sm">
                  {(t.logo?.light ?? t.logo?.dark) ? (
                    <img src={t.logo?.light ?? t.logo?.dark} alt="" className="h-full w-full object-contain" />
                  ) : (
                    <Text size="1" weight="bold" color="gray">
                      {t.agentName?.[0] || t.name?.[0] || 'H'}
                    </Text>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <Text size="2" weight={isActive ? 'bold' : 'medium'} truncate>
                      {t.name}
                    </Text>
                    {isModified && <div className="h-2 w-2 rounded-full bg-blue-500"></div>}
                  </div>
                  {t.applyToClient && (
                    <Badge variant="surface" className="mt-1">
                      Client
                    </Badge>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
